import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "motion/react";
import { Icon, type IconName } from "./Icon";
import { haptic } from "../telegram/haptics";
import { pressSpring } from "../motion";
import { useT } from "../i18n/strings";
import s from "./AppShell.module.css";

/** Copy is resolved per render; only the route and glyph are fixed here. */
const TABS: Array<{ to: string; icon: IconName; key: "works" | "browse" | "add" }> = [
  { to: "/", icon: "works", key: "works" },
  { to: "/browse", icon: "browse", key: "browse" },
  { to: "/new", icon: "add", key: "add" },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  // The form owns the whole screen: its sticky Save sits where the tabs would.
  const showTabs = !isFormRoute(pathname) && !pathname.startsWith("/artwork/");

  return (
    <div className={s.shell}>
      <div className={s.main}>{children}</div>
      {showTabs && <TabBar />}
    </div>
  );
}

function isFormRoute(pathname: string) {
  return pathname === "/new" || pathname.endsWith("/edit");
}

function TabBar() {
  const t = useT();
  return (
    <nav className={`${s.tabbar} chrome`} aria-label={t.appName}>
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.to === "/"}
          onClick={() => haptic.tap()}
          className={({ isActive }) => [s.tab, isActive && s.tabActive].filter(Boolean).join(" ")}
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <motion.span layoutId="tab-mark" className={s.tabMark} transition={pressSpring} />
              )}
              {/* The glyph lifts a hair as the tab takes over, so the change of
                  tab is felt in the icon and not only in its colour. */}
              <motion.span
                className={s.tabIcon}
                animate={{ scale: isActive ? 1.08 : 1, y: isActive ? -1 : 0 }}
                transition={pressSpring}
              >
                <Icon name={tab.icon} size={24} />
              </motion.span>
              <span>{t.tabs[tab.key]}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** The scroll container for one route. Routes never scroll the shell itself. */
export function Screen({
  children,
  withTabs = true,
  scrollRef,
}: {
  children: ReactNode;
  withTabs?: boolean;
  scrollRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div ref={scrollRef} className={[s.scroller, withTabs && s.withTabs].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

export function ScreenHeader({
  title,
  actions,
  sticky,
  bordered,
  overlay,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  sticky?: boolean;
  bordered?: boolean;
  /** Floats the row over the content beneath it — a hero, never a list. */
  overlay?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={[
        sticky && s.stickyTop,
        sticky && "chrome",
        bordered && s.headerBorder,
        overlay && s.headerOverlay,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className={s.header}>
        {title !== undefined && <h1 className={s.headerTitle}>{title}</h1>}
        {actions && <div className={s.headerActions}>{actions}</div>}
      </header>
      {children}
    </div>
  );
}

/** The 48px madder button in the thumb zone. One per screen, never two. */
export function StickyCta({ children }: { children: ReactNode }) {
  return <div className={`${s.ctaBar} chrome`}>{children}</div>;
}

export function Page({ children }: { children: ReactNode }) {
  return <div className={s.page}>{children}</div>;
}

export function Section({
  title,
  icon,
  children,
}: {
  title?: string;
  icon?: IconName;
  children: ReactNode;
}) {
  return (
    <section className={s.section}>
      {title && (
        <div className={s.sectionHead}>
          {icon && <Icon name={icon} size={16} />}
          <h2 className="section-label">{title}</h2>
          <span className={s.sectionRule} aria-hidden />
        </div>
      )}
      <div className={`${s.sectionCard} paper`}>{children}</div>
    </section>
  );
}

export { s as shellStyles };
